import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Upload, Button, message, Modal, Space, Typography } from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';
import FilePreviewModal from './FilePreviewModal';

const { Dragger } = Upload;
const { Text } = Typography;

interface CustomFileUploadProps {
  value?: File[];
  onChange?: (files: File[]) => void;
  multiple?: boolean;
  maxCount?: number;
  maxSizeMB?: number;
  accept?: string;
  disabled?: boolean;
  existingFiles?: string[]; 
  onRemoveExisting?: (url: string) => void;
  showUploadList?: boolean;
  listType?: 'text' | 'picture' | 'picture-card';
  dragAndDrop?: boolean;
}

const CustomFileUpload: React.FC<CustomFileUploadProps> = ({
  value = [],
  onChange,
  multiple = true,
  maxCount = multiple ? 5 : 1,
  maxSizeMB = 5,
  accept = '.pdf,.jpg,.jpeg,.png',
  disabled = false,
  existingFiles = [],
  onRemoveExisting,
  showUploadList = true,
  listType = 'text',
  dragAndDrop = true,
}) => {
  const { t } = useTranslation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>('');

  const filesRef = useRef<File[]>(value);

  useEffect(() => {
    filesRef.current = value;
  }, [value]);

  const maxSizeBytes = useMemo(() => maxSizeMB * 1024 * 1024, [maxSizeMB]);

  const allowedMaxCount = multiple ? maxCount : 1;

  // Check if we can add more files from a given files array
  const canAddMoreFilesFrom = (currentFiles: File[]) => {
    const totalFiles = currentFiles.length + existingFiles.length;
    return totalFiles < allowedMaxCount;
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const currentFiles = filesRef.current;

    if (file.size > maxSizeBytes) {
      message.error(
        t('moduleFileUpload.fileSizeExceedsLimit', { maxSize: maxSizeMB })
      );
      return Upload.LIST_IGNORE;
    }

    if (!canAddMoreFilesFrom(currentFiles)) {
      message.error(
        multiple
          ? t('moduleFileUpload.maxFileCountExceeded', { maxCount: allowedMaxCount })
          : t('moduleFileUpload.singleFileReached')
      );
      return Upload.LIST_IGNORE;
    }

    const newFiles = multiple ? [...currentFiles, file] : [file];

    filesRef.current = newFiles;
    onChange?.(newFiles);

    return false;
  };

  const canAddMoreFiles = () => {
    if (multiple) {
      const totalFiles = value?.length + existingFiles.length;
      return totalFiles < allowedMaxCount;
    } else {
      return value?.length === 0 && existingFiles.length === 0;
    }
  };

  const handleRemove = (index: number) => {
    const newFiles = value.filter((_, i) => i !== index);
    filesRef.current = newFiles;
    onChange?.(newFiles);
  };

  const handleRemoveExisting = (url: string) => {
    onRemoveExisting?.(url);
  };

  const handlePreview = (url: string, name?: string) => {
    setPreviewUrl(url);
    setPreviewName(name || url.split('/').pop() || 'File');
    setPreviewVisible(true);
  };

  const getFileName = (item: File | string) => {
    if (item instanceof File) {
      return item.name;
    }
    return item.split('/').pop() || 'File';
  };

  const getFileSize = (file: File) => {
    return (file.size / 1024).toFixed(1);
  };

  const isMaxReached = !canAddMoreFiles();

  const hasFile = value.length > 0 || existingFiles.length > 0;

  const uploadProps: UploadProps = {
    name: 'attachments',
    multiple,
    accept,
    beforeUpload,
    showUploadList: false,
    disabled: disabled || isMaxReached,
    fileList: [],
    listType,
  };

  return (
    <>
      {/* File list display */}
      {showUploadList && hasFile && (
        <div
          style={{
            marginBottom: 16,
            border: listType === 'picture-card' ? 'none' : '1px solid #d9d9d9',
            borderRadius: 6,
            display: listType === 'picture-card' ? 'flex' : 'block',
            flexWrap: listType === 'picture-card' ? 'wrap' : 'nowrap',
            gap: listType === 'picture-card' ? 8 : 0,
          }}
        >
          {/* Existing files (URLs) */}
          {existingFiles.map((url, index) => {
            const fileName = getFileName(url);
            const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)$/i);

            if (listType === 'picture-card') {
              return (
                <div
                  key={`existing-${index}`}
                  style={{ position: 'relative', width: 100, height: 100 }}
                >
                  {isImage ? (
                    <img
                      src={url}
                      alt={fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <PaperClipOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <Text
                        ellipsis
                        style={{ fontSize: 10, textAlign: 'center', padding: '0 4px' }}
                      >
                        {fileName}
                      </Text>
                    </div>
                  )}
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255,255,255,0.8)',
                    }}
                    onClick={() => handleRemoveExisting(url)}
                    disabled={disabled}
                  />
                </div>
              );
            }

            return (
              <div
                key={`existing-${index}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px',
                  borderBottom:
                    multiple &&
                      (index < existingFiles.length - 1 || value.length > 0)
                      ? '1px solid #f0f0f0'
                      : 'none',
                }}
              >
                <Space>
                  <PaperClipOutlined />
                  <Text
                    ellipsis
                    style={{ maxWidth: 200, cursor: 'pointer' }}
                    onClick={() => handlePreview(url, fileName)}
                  >
                    {fileName}
                  </Text>
                  {!multiple && (
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                      ({t('moduleFileUpload.singleFileMode')})
                    </Text>
                  )}
                </Space>
                <Space>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handlePreview(url, fileName)}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleRemoveExisting(url)}
                    disabled={disabled}
                  />
                </Space>
              </div>
            );
          })}

          {/* Newly uploaded files */}
          {value.map((file, index) => {
            const fileName = getFileName(file);
            const isImage = file.type?.startsWith('image/');

            if (listType === 'picture-card') {
              const localPreviewUrl = URL.createObjectURL(file);
              return (
                <div
                  key={index}
                  style={{ position: 'relative', width: 100, height: 100 }}
                >
                  {isImage ? (
                    <img
                      src={localPreviewUrl}
                      alt={fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <PaperClipOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <Text
                        ellipsis
                        style={{ fontSize: 10, textAlign: 'center', padding: '0 4px' }}
                      >
                        {fileName}
                      </Text>
                    </div>
                  )}
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255,255,255,0.8)',
                    }}
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                  />
                </div>
              );
            }

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px',
                  borderBottom:
                    multiple && index < value.length - 1
                      ? '1px solid #f0f0f0'
                      : 'none',
                }}
              >
                <Space>
                  <PaperClipOutlined />
                  <Text
                    ellipsis
                    style={{ maxWidth: 200, cursor: 'pointer' }}
                    onClick={() => {
                      const localPreviewUrl = URL.createObjectURL(file);
                      handlePreview(localPreviewUrl, fileName);
                    }}
                  >
                    {fileName}
                  </Text>
                  <Text type="secondary">({getFileSize(file)} KB)</Text>
                  {!multiple && (
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                      ({t('moduleFileUpload.singleFileMode')})
                    </Text>
                  )}
                </Space>
                <Space>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => {
                      const localPreviewUrl = URL.createObjectURL(file);
                      handlePreview(localPreviewUrl, fileName);
                    }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                  />
                </Space>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload area */}
      {!isMaxReached && (
        <div>
          {dragAndDrop ? (
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                {multiple ? <InboxOutlined /> : <UploadOutlined />}
              </p>
              <p className="ant-upload-text">
                {multiple
                  ? t('moduleFileUpload.clickOrDragToUpload')
                  : hasFile
                    ? t('moduleFileUpload.replaceFile')
                    : t('moduleFileUpload.clickOrDragSingleFile')}
              </p>
              <p className="ant-upload-hint">
                {t('moduleFileUpload.maxFileSizeToUpload', { maxSize: maxSizeMB })}
                {' | '}
                {multiple
                  ? t('moduleFileUpload.maxFiles', { maxCount: allowedMaxCount })
                  : t('moduleFileUpload.singleFileOnly')}
                {' | '}
                {t('moduleFileUpload.supportedFormats', { formats: accept })}
              </p>
              {!multiple && hasFile && (
                <p
                  className="ant-upload-hint"
                  style={{ color: '#faad14', marginTop: 8 }}
                >
                  {t('moduleFileUpload.uploadingWillReplaceExisting')}
                </p>
              )}
            </Dragger>
          ) : (
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {multiple
                  ? t('moduleFileUpload.selectFiles')
                  : hasFile
                    ? t('moduleFileUpload.replaceFile')
                    : t('moduleFileUpload.selectFile')}
              </Button>
            </Upload>
          )}
        </div>
      )}

      {isMaxReached && !dragAndDrop && (
        <div style={{ marginTop: 8, color: '#999' }}>
          {multiple
            ? t('moduleFileUpload.maxFilesReached', { maxCount: allowedMaxCount })
            : t('moduleFileUpload.singleFileReached')}
        </div>
      )}
      <FilePreviewModal
        visible={previewVisible}
        url={previewUrl}
        fileName={previewName}
        onClose={() => {
          setPreviewVisible(false);
          setPreviewUrl(null);
        }}
      />
    </>
  );
};

export default CustomFileUpload;
